import type { Instance } from '@rbf/shared';

/**
 * The run header fields that decide whether a write is part of the sealed record.
 * `status` is a bare nullable string, not the narrowed Instance union: PowerSync
 * hands back a local SQLite row, where it is text, and the run may not have loaded.
 */
export type SealedRun = Pick<Instance, 'completed_at' | 'completed_recorded_at'> & {
  status: string | null;
};

/**
 * Was this check or note NOT accounted for by the run's sealed record?
 *
 * Every run record carries two instants: the AUTHORED time (when the pilot did it,
 * stamped on-device and kept even when it uploads hours later) and the server-owned
 * RECORDED time (when the server learned of it). The run header carries the same
 * pair. A write escapes the seal if EITHER clock says so, and the two clauses catch
 * genuinely different things:
 *
 *  1. Recorded after the server froze the run. The pilot checked a step offline at
 *     09:50, but the PATCH only landed at 14:05 — after the run was sealed and
 *     frozen. Done in time, synced too late: the sealed record never saw it. Both
 *     operands are server-owned, so this clause cannot be skewed by a device clock.
 *
 *  2. Authored after the run was sealed. A second member who is still ONLINE can
 *     write to a run whose offline seal has not uploaded yet — the server thinks it
 *     is active and takes the write. Their 11:00 check reaches the server hours
 *     before the 10:00 seal does, so clause 1 calls it on time; but it was made an
 *     hour after the run was actually sealed.
 *
 * The clauses are disjoint, not belt-and-braces. clamp_authored_at (see
 * 20260707000000) already bounds an authored time above by the run's completed_at
 * for any write arriving AFTER the seal is known, so clause 2 can only ever fire for
 * a write recorded BEFORE the seal uploaded — precisely the partition above. It
 * cannot fire on an ordinary run.
 *
 * Its cost, accepted deliberately: clause 2 compares one device's clock against
 * another's, so on a multi-member offline run enough cross-device skew can raise a
 * false badge. Clause 1 is skew-proof and clause 2 is not; the alternative was
 * staying blind to the partition case entirely.
 */
export function escapedSeal(
  run: SealedRun,
  recordedAt: string | null | undefined,
  authoredAt: string | null | undefined,
): boolean {
  if (run.status !== 'completed') return false;

  const after = (a: string, b: string) => new Date(a).getTime() > new Date(b).getTime();

  if (
    recordedAt &&
    run.completed_recorded_at &&
    after(recordedAt, run.completed_recorded_at)
  )
    return true;
  if (authoredAt && run.completed_at && after(authoredAt, run.completed_at)) return true;

  return false;
}

/**
 * Under this, the seal uploaded as good as instantly — a gap not worth reporting.
 * Deliberately tight: a seal that took even a quarter of a minute to reach the server
 * did NOT go up with the run, and that is worth saying. The threshold only exists to
 * swallow the round trip itself.
 *
 * The gap is measured across two clocks (the device's authored seal against the
 * server's ingest stamp), so a device running slow can manufacture one, and a device
 * running fast can shrink or hide a real one (never past zero on screen: the server
 * clamps a future authored seal down to its own now(), so the gap can't go visibly
 * negative). That is the accepted cost of the tight bound: an over-eager or missing
 * provenance line, never a wrong seal time — the authored instant on the card stands
 * either way.
 */
const PROMPT_UPLOAD_MS = 15_000;

/**
 * When the server learned of the seal, but only if that meaningfully lags the seal
 * itself — the seal did not go up with the run, typically because it was made with
 * no signal. The authored seal is the fact of record; this is its provenance, and
 * worth showing precisely because the two are otherwise indistinguishable on screen.
 *
 * Null when there is nothing to say: the run isn't sealed, the seal uploaded
 * promptly, or it hasn't uploaded at all yet — completed_recorded_at is server-owned,
 * so it stays null on the local row until the seal round-trips, which is the window
 * the run screen already covers with its "Not synced yet" banner.
 */
export function delayedSealUpload(run: SealedRun): string | null {
  if (run.status !== 'completed') return null;
  const { completed_at: authored, completed_recorded_at: recorded } = run;
  if (!authored || !recorded) return null;

  const gap = new Date(recorded).getTime() - new Date(authored).getTime();
  return gap > PROMPT_UPLOAD_MS ? recorded : null;
}
